/** Work order list with URL-backed filters, sorting, pagination and a create dialog. */
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { AssetPicker } from "@/features/assets/AssetPicker";
import { WorkOrderAssignees } from "@/features/workorders/WorkOrderAssignees";
import {
  WO_PRIORITIES,
  WO_TYPES,
  type WorkOrder,
  type WorkOrderFilters,
  type WorkOrderPlanningFields,
  useCreateWorkOrder,
  useWorkOrderAssetDefaults,
  useWorkOrderPage,
} from "@/features/workorders/queries";
import { WorkOrderPlanningFields as PlanningFields } from "@/features/workorders/WorkOrderPlanningFields";
import { WorkOrderTableControls } from "@/features/workorders/WorkOrderTableControls";
import { useSites } from "@/features/admin/reference/queries";
import { DataTable } from "@/shared/components/data-table";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { formatDateTime } from "@/shared/lib/format";
import { PAGE_SIZES, useListState } from "@/shared/lib/list-state";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Dialog } from "@/shared/ui/primitives";

/** Dialog to create a corrective/other work order. */
export function NewWorkOrderDialog({
  open,
  onOpenChange,
  defaultAssetId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultAssetId?: string;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateWorkOrder();
  const [assetId, setAssetId] = useState(defaultAssetId ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<string>("corrective");
  const [priority, setPriority] = useState<string>("normal");
  const [outOfService, setOutOfService] = useState(false);
  const [planning, setPlanning] = useState<WorkOrderPlanningFields>({});
  const assetDefaults = useWorkOrderAssetDefaults(assetId);
  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors() : {};

  const updatePlanning = <K extends keyof WorkOrderPlanningFields>(
    field: K,
    value: WorkOrderPlanningFields[K],
  ): void => setPlanning((current) => ({ ...current, [field]: value }));

  const submit = (e: { preventDefault(): void }): void => {
    e.preventDefault();
    create.mutate(
      {
        asset_id: assetId,
        title,
        description,
        type,
        priority,
        asset_out_of_service: outOfService,
        ...planning,
        restriction_scope: planning.restriction_scope ?? "component",
      },
      {
        onSuccess: (wo) => {
          onOpenChange(false);
          void navigate({ to: "/work-orders/$woId", params: { woId: wo.id } });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t("workOrders.new")}>
      <form onSubmit={submit} className="space-y-4">
        <Field id="wo-asset" label={t("app.asset")} error={fieldErrors.asset_id}>
          <AssetPicker id="wo-asset" value={assetId} onChange={setAssetId} />
        </Field>
        <Field id="wo-title" label={t("app.title")} error={fieldErrors.title}>
          <Input id="wo-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field id="wo-description" label={t("app.description")}>
          <Textarea
            id="wo-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field id="wo-type" label={t("app.type")}>
            <Select id="wo-type" value={type} onChange={(e) => setType(e.target.value)}>
              {WO_TYPES.map((v) => (
                <option key={v} value={v}>
                  {t(`workOrders.type.${v}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field id="wo-priority" label={t("app.priority")}>
            <Select id="wo-priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {WO_PRIORITIES.map((v) => (
                <option key={v} value={v}>
                  {t(`workOrders.priority.${v}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Checkbox
          id="wo-oos"
          label={t("workOrders.assetOutOfService")}
          checked={outOfService}
          onChange={(e) => setOutOfService(e.target.checked)}
        />
        <details className="rounded-md border px-3 py-2">
          <summary className="cursor-pointer font-medium">
            {t("workOrders.planningAllocation")}
          </summary>
          <div className="pt-3">
            <PlanningFields
              values={planning}
              onChange={updatePlanning}
              defaults={assetDefaults.data}
              prefix="new-wo-planning"
            />
          </div>
        </details>
        {create.isError && Object.keys(fieldErrors).length === 0 ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(create.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={create.isPending || !assetId || !title}>
            {t("app.create")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Work order list page. */
export function WorkOrdersPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const list = useListState("/work-orders", "-created_at");
  const { search, state } = list;
  // A deep link with `asset_id` opens the create dialog pre-filled.
  const [createOpen, setCreateOpen] = useState(Boolean(search.asset_id));
  const openOnly = search.status ? false : (search.open_only ?? true);
  const filters: WorkOrderFilters = {
    q: search.q,
    status: search.status,
    open_only: openOnly,
    type: search.type,
    priority: search.priority,
    mine: search.mine,
    site_id: search.site_id,
    overdue: search.overdue,
    due_after: search.due_after,
    due_before: search.due_before,
    completed_after: search.completed_after,
    completed_before: search.completed_before,
  };
  const page = useWorkOrderPage(filters, state);
  const sites = useSites();

  const columns: ColumnDef<WorkOrder>[] = [
    {
      id: "code",
      enableSorting: true,
      header: t("app.code"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.code}</span>,
    },
    {
      id: "title",
      enableSorting: true,
      header: t("app.title"),
      cell: (c) => <span className="font-medium">{c.row.original.title}</span>,
    },
    {
      id: "asset",
      enableSorting: true,
      header: t("app.asset"),
      cell: (c) => (
        <AssetReference
          id={c.row.original.asset.id}
          name={c.row.original.asset.name}
          context="work-order"
        />
      ),
    },
    {
      id: "type",
      enableSorting: true,
      header: t("app.type"),
      cell: (c) => t(`workOrders.type.${c.row.original.type}`),
    },
    {
      id: "priority",
      enableSorting: true,
      header: t("app.priority"),
      cell: (c) => <StatusBadge value={c.row.original.priority} namespace="workOrders.priority" />,
    },
    {
      id: "status",
      enableSorting: true,
      header: t("app.status"),
      cell: (c) => <StatusBadge value={c.row.original.status} namespace="workOrders.status" />,
    },
    {
      id: "due_at",
      enableSorting: true,
      header: t("app.due"),
      cell: (c) => (
        <span className={c.row.original.is_overdue ? "font-medium text-destructive" : undefined}>
          {formatDateTime(c.row.original.due_at)}
        </span>
      ),
    },
    {
      id: "assignees",
      header: t("workOrders.assignees"),
      cell: (c) => (
        <WorkOrderAssignees
          workOrderId={c.row.original.id}
          status={c.row.original.status}
          assignees={c.row.original.assignees}
          compact
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t("workOrders.title")}
        actions={
          can("workorders.workorder.create") ? (
            <Button onClick={() => setCreateOpen(true)}>{t("workOrders.new")}</Button>
          ) : null
        }
      />
      {createOpen && can("workorders.workorder.create") ? (
        <NewWorkOrderDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          defaultAssetId={search.asset_id}
        />
      ) : null}
      <WorkOrderTableControls
        filters={filters}
        sites={(sites.data ?? []).map((site) => ({ value: site.id, label: site.name }))}
        sorting={state.sort}
        onChange={list.update}
        onSortChange={list.setSort}
        onReset={() =>
          list.update({
            q: undefined,
            status: undefined,
            priority: undefined,
            type: undefined,
            site_id: undefined,
            mine: undefined,
            overdue: undefined,
            due_after: undefined,
            due_before: undefined,
            completed_after: undefined,
            completed_before: undefined,
            open_only: undefined,
          })
        }
      />
      {page.query.isPending ? (
        <LoadingState />
      ) : page.query.isError ? (
        <ErrorState error={page.query.error} onRetry={() => void page.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={page.items}
          minWidth={1024}
          getRowId={(w) => w.id}
          rowHref={(w) => `/work-orders/${w.id}`}
          sorting={state.sort}
          onSortingChange={list.setSort}
          isFetching={page.query.isFetching}
          pagination={{
            page: page.page,
            pages: page.pages,
            total: page.total,
            pageSize: page.pageSize,
            onPageChange: list.setPage,
            onPageSizeChange: list.setSize,
            sizes: PAGE_SIZES,
            extendedNavigation: true,
          }}
        />
      )}
    </div>
  );
}
